import { createLogger } from "@/logger";
import { BootstrapEventsRegistry } from "@/models/BootstrapEventsRegistry";
import { recordEvent } from "@/services/telemetry";
import { snapshotRestoreFailed } from "@/store/snapshot";
import { store } from "@/store/store";

const log = createLogger("storage");

/**
 * Collections whose silent loss breaks either the channel list or driver roster. `serverKey`
 * matches `BootstrapEventListType.tableName` (also the store collection name).
 */
const PROBED_COLLECTIONS = ["feed", "permission", "feedPreference", "account", "workspaceMembership"] as const;

type TableDivergence = {
  /** Rows the server returned for this collection during the last bootstrap run. */
  serverRows: number;
  /** Local row count. */
  localRows: number;
};

type StoreInvariantProbe = {
  /** A membership references an account row that is missing locally. */
  brokenAccountRefs: boolean;
  /** A permission or alias feed references a membership that is missing. */
  brokenMembershipRefs: boolean;
  /** A permission references a feed that is missing locally. */
  brokenPermissionFeedRefs: boolean;
  /** An item references a feed that is missing locally. */
  brokenItemFeedRefs: boolean;
  /** A membership tag references a tag row that is missing locally. */
  brokenTagRefs: boolean;
  /** A feed group membership references a feed group that is missing locally. */
  brokenFeedGroupRefs: boolean;
  /**
   * Feeds whose trusted contiguous span claims history their surviving items no longer cover —
   * either the oldest local item sits above the span floor, or the span is set with no items at
   * all. See {@link probeItemCoverage} for why eviction cannot produce this shape.
   */
  feedsWithContiguityHole: number;
};

export type DivergenceProbeResult = {
  diverged: boolean;
  perTable: Record<string, TableDivergence>;
  referentialIntegrity: StoreInvariantProbe;
};

export type DivergenceRecoveryOutcome =
  | { status: "consistent" }
  | { status: "superseded" }
  | { status: "rebootstrapped" };

/** Adds one bootstrap page's row count to the per-table server totals. */
export function addServerRowCount(counts: Map<string, number>, tableName: string, rows: number): void {
  counts.set(tableName, (counts.get(tableName) ?? 0) + rows);
}

/**
 * Checks the reference invariants across the whole store. Diagnostic only — never a divergence
 * trigger: the store is an app-wide singleton that legitimately holds rows outside the bootstrap's
 * scope (other workspaces after a client-side switch, other members' permissions from the feed
 * loaders), while bootstrap fetches `mine: true`, so broken refs accumulate by construction on a
 * long-lived client — and no recovery tier can delete the orphans anyway (WEB-2248).
 *
 * The scope argument covers the checks added for the partial-hole gap (WEB-2241) too, so they are
 * measurements, not rules: over the 30 days before WEB-2248 every single `storage.divergence` event
 * in prod was a broken-ref firing with no empty collection — a 100% false-positive rate that cost
 * 30 wipe-and-reloads — so no invariant gates recovery until a healthy-boot baseline says which of
 * these hold on a working client. {@link recoverFromBootstrapDivergence} emits that baseline.
 */
function probeReferentialIntegrity(): StoreInvariantProbe {
  let brokenAccountRefs = false;
  for (const membership of store.getAll("workspaceMembership").values()) {
    if (membership.accountId != null && store.get("account", membership.accountId) === undefined) {
      brokenAccountRefs = true;
      break;
    }
  }
  let brokenMembershipRefs = false;
  let brokenPermissionFeedRefs = false;
  for (const permission of store.getAll("permission").values()) {
    if (
      !brokenMembershipRefs &&
      permission.workspaceMembershipId != null &&
      store.get("workspaceMembership", permission.workspaceMembershipId) === undefined
    ) {
      brokenMembershipRefs = true;
    }
    if (!brokenPermissionFeedRefs && permission.feedId != null && store.get("feed", permission.feedId) === undefined) {
      brokenPermissionFeedRefs = true;
    }
    if (brokenMembershipRefs && brokenPermissionFeedRefs) break;
  }
  if (!brokenMembershipRefs) {
    for (const feed of store.getAll("feed").values()) {
      if (feed.aliasMembershipId != null && store.get("workspaceMembership", feed.aliasMembershipId) === undefined) {
        brokenMembershipRefs = true;
        break;
      }
    }
  }
  let brokenTagRefs = false;
  for (const membershipTag of store.getAll("workspaceMembershipTag").values()) {
    if (store.get("tag", membershipTag.tagId) === undefined) {
      brokenTagRefs = true;
      break;
    }
  }
  let brokenFeedGroupRefs = false;
  for (const groupMembership of store.getAll("feedGroupMembership").values()) {
    if (store.get("feedGroup", groupMembership.groupId) === undefined) {
      brokenFeedGroupRefs = true;
      break;
    }
  }
  return {
    brokenAccountRefs,
    brokenMembershipRefs,
    brokenPermissionFeedRefs,
    brokenTagRefs,
    brokenFeedGroupRefs,
    ...probeItemCoverage(),
  };
}

/**
 * One pass over `item` deriving the two signals that need per-feed item extents: items whose feed
 * is missing, and feeds whose contiguous span outruns the items backing it.
 *
 * The span signal is the shape a partial hole leaves. `oldestContiguousCreatedAt` marks the oldest
 * item proven contiguous with the live tail, so an oldest surviving item *above* the floor means
 * rows inside the trusted span are gone. Deliberate local removal cannot produce it: eviction and
 * content retention both prune through `deleteItemsAndOrphanedContent`, which walks the floor
 * forward onto the oldest survivor (or clears the span outright), so a pruned feed lands exactly on
 * the floor rather than above it. Rows *below* the floor are the legitimate case — untrusted
 * history pulled in by a deeplink jump — and are not counted.
 *
 * It only sees loss at the span's bottom edge: a hole in the middle of a span, or one confined to
 * rows above the ceiling that the subscription delivered, leaves both bounds intact.
 */
function probeItemCoverage(): Pick<StoreInvariantProbe, "brokenItemFeedRefs" | "feedsWithContiguityHole"> {
  let brokenItemFeedRefs = false;
  const oldestItemByFeed = new Map<string, number>();
  for (const item of store.getAll("item").values()) {
    if (!brokenItemFeedRefs && store.get("feed", item.feedId) === undefined) brokenItemFeedRefs = true;
    const createdAt = new Date(item.createdAt).getTime();
    if (Number.isNaN(createdAt)) continue;
    const oldest = oldestItemByFeed.get(item.feedId);
    if (oldest === undefined || createdAt < oldest) oldestItemByFeed.set(item.feedId, createdAt);
  }
  let feedsWithContiguityHole = 0;
  for (const feed of store.getAll("feed").values()) {
    if (feed.oldestContiguousCreatedAt == null) continue;
    const floor = new Date(feed.oldestContiguousCreatedAt).getTime();
    if (Number.isNaN(floor)) continue;
    const oldestItem = oldestItemByFeed.get(feed.id);
    // A span with no surviving items at all is the same shape: it was folded in from a fetched
    // page, and eviction clears the span when nothing survives.
    if (oldestItem === undefined || oldestItem > floor) feedsWithContiguityHole += 1;
  }
  return { brokenItemFeedRefs, feedsWithContiguityHole };
}

/**
 * Compares server-returned bootstrap row counts against local collection sizes. Divergence is
 * strict — the server returned rows this session but the local collection is empty — so a
 * legitimately empty workspace (zero server rows) never trips it. The referential probe rides
 * along as a diagnostic (see {@link probeReferentialIntegrity} for why it must not trigger). With
 * ingest fully in memory this now guards against ingest bugs and interrupted boots rather than
 * storage-engine row loss.
 */
export function probeBootstrapDivergence(serverCounts: ReadonlyMap<string, number>): DivergenceProbeResult {
  const perTable: Record<string, TableDivergence> = {};
  let diverged = false;
  for (const collection of PROBED_COLLECTIONS) {
    const serverRows = serverCounts.get(collection) ?? 0;
    const localRows = store.size(collection);
    perTable[collection] = { serverRows, localRows };
    if (serverRows > 0 && localRows === 0) diverged = true;
  }
  return { diverged, perTable, referentialIntegrity: probeReferentialIntegrity() };
}

/** Tier-1 guard: the in-place re-bootstrap runs at most once per workspace per page load. */
const inPlaceRebootstrapAttempted = new Set<string>();

/** Test-only reset of the per-page-load recovery guards. */
export function resetDivergenceRecoveryState(): void {
  inPlaceRebootstrapAttempted.clear();
}

/**
 * Probes for bootstrap divergence and, when found, re-bootstraps in place once.
 *
 * The single remedy clears the workspace's bootstrap-registry timestamps and re-runs a full
 * bootstrap in place — no reload, no snapshot delete, nothing the user can lose — at most once per
 * workspace per page load. A divergence that survives it is reported and nothing further happens:
 * the store stays as it is, the app runs on what it has, and the next boot probes again.
 *
 * There is deliberately no escalation beyond that (WEB-2252). The wipe-and-reload and StorageError
 * tiers this replaces never fixed a divergence the probe caught for real — in 30 days of prod
 * telemetry the empty-collection rule fired zero times, while the referential rule fired 280 times
 * at a 100% false-positive rate and wiped 30 users' caches. A wipe cannot fix the failure this
 * probe can actually see, an ingest bug, because the next boot runs the same code; and an
 * unreadable snapshot already self-heals in `restoreSnapshot` without destroying anything.
 */
export async function recoverFromBootstrapDivergence({
  serverCounts,
  workspaceId,
  bootstrapMode,
  superseded,
  rebootstrap,
}: {
  serverCounts: ReadonlyMap<string, number>;
  workspaceId: string | null;
  bootstrapMode: "full" | "incremental";
  superseded?: () => boolean;
  rebootstrap: () => Promise<void>;
}): Promise<DivergenceRecoveryOutcome> {
  if (serverCounts.size === 0) return { status: "consistent" };

  const probe = probeBootstrapDivergence(serverCounts);
  if (superseded?.()) return { status: "superseded" };
  // Every run, healthy included: a rule can only be judged against the distribution of these
  // signals on boots that were fine, and no existing event carries them — `storage.divergence`
  // records its count pairs only once it has already fired. One event per bootstrap is ~1k/day
  // fleet-wide, which is why the category is passed explicitly as the unsampled `reliability`
  // rather than `diagnostic` (rate 0, so it would ship nothing at all). Recording only; nothing
  // here decides divergence.
  //
  // Tier-1 recovery re-runs the bootstrap and so re-runs this probe, meaning one diverged boot
  // emits two or more of these. A baseline query must filter on `diverged=false` rather than treat
  // every event as an independent sample.
  recordEvent(
    "storage.divergence.diagnostics",
    {
      storage: {
        workspaceId,
        bootstrapMode,
        diverged: probe.diverged,
        counts: probe.perTable,
        invariants: probe.referentialIntegrity,
      },
    },
    "reliability",
  );
  const { brokenAccountRefs, brokenMembershipRefs } = probe.referentialIntegrity;
  if (brokenAccountRefs || brokenMembershipRefs) {
    recordEvent(
      "storage.divergence.broken_refs",
      { storage: { workspaceId, bootstrapMode, brokenAccountRefs, brokenMembershipRefs } },
      "diagnostic",
    );
  }
  const rebootstrapAttempted = inPlaceRebootstrapAttempted.has(workspaceId ?? "unknown");
  if (!probe.diverged) {
    if (rebootstrapAttempted) {
      log.info("[DivergenceRecovery] Store consistent after in-place re-bootstrap");
      recordEvent("storage.divergence.recovered", {
        storage: { workspaceId, bootstrapMode, recovery: "in-place-rebootstrap" },
      });
    }
    return { status: "consistent" };
  }

  log.error("[DivergenceProbe] Store is missing rows the server returned", {
    perTable: probe.perTable,
    referentialIntegrity: probe.referentialIntegrity,
    workspaceId,
    bootstrapMode,
    rebootstrapAttempted,
  });
  recordEvent("storage.divergence", {
    storage: { workspaceId, bootstrapMode, rebootstrapAttempted, detail: summarize(probe) },
  });

  // This load's restore already failed on a broken backing store, so the boot fell back to a full
  // bootstrap. Re-bootstrapping would buy a second full fetch of the same rows and change nothing
  // (WEB-2251). Scoped to restore failures on purpose: a client that restored warm and later
  // cannot *persist* still has a working in-memory store, and the re-fetch would help it.
  if (snapshotRestoreFailed()) {
    log.info("[DivergenceRecovery] Skipping re-bootstrap: this load's snapshot restore failed");
    recordEvent("storage.divergence.skipped", {
      storage: { workspaceId, bootstrapMode, reason: "snapshot-restore-failed" },
    });
    return { status: "consistent" };
  }

  // Already tried once this page load: report and leave the store alone. `consistent` is the
  // caller's "nothing more to do" — it has no action left to take, and there is no screen to show.
  if (rebootstrapAttempted) return { status: "consistent" };

  inPlaceRebootstrapAttempted.add(workspaceId ?? "unknown");
  try {
    await BootstrapEventsRegistry.getInstance().clearWorkspace(workspaceId ?? undefined);
  } catch (error) {
    // A full re-bootstrap needs timestamp=undefined; without the registry clear it may stay
    // incremental, in which case the divergence persists and the next probe reports it again.
    log.warn("[DivergenceRecovery] Failed to clear bootstrap registry", { error });
  }
  await rebootstrap();
  return { status: "rebootstrapped" };
}

/**
 * Divergence detail for the `storage.divergence` event. Names the collections that actually
 * triggered — server rows with an empty local collection — and labels everything after them as
 * diagnostics: the raw count pairs and referential flags never decide divergence, and unlabeled
 * they have misled triage into reading count gaps as the trigger (WEB-2248).
 */
function summarize({ perTable, referentialIntegrity }: DivergenceProbeResult): string {
  const emptyCollections = Object.entries(perTable)
    .filter(([, { serverRows, localRows }]) => serverRows > 0 && localRows === 0)
    .map(([table]) => table);
  const counts = Object.entries(perTable).map(
    ([table, { serverRows, localRows }]) => `${table} server=${serverRows} local=${localRows}`,
  );
  return [
    `triggered by empty local collections [${emptyCollections.join(", ")}]`,
    `diagnostics only: ${counts.join(", ")}`,
    `broken_account_refs=${referentialIntegrity.brokenAccountRefs}`,
    `broken_membership_refs=${referentialIntegrity.brokenMembershipRefs}`,
    `broken_permission_feed_refs=${referentialIntegrity.brokenPermissionFeedRefs}`,
    `broken_item_feed_refs=${referentialIntegrity.brokenItemFeedRefs}`,
    `broken_tag_refs=${referentialIntegrity.brokenTagRefs}`,
    `broken_feed_group_refs=${referentialIntegrity.brokenFeedGroupRefs}`,
    `feeds_with_contiguity_hole=${referentialIntegrity.feedsWithContiguityHole}`,
  ].join("; ");
}
